import { useEffect } from 'react';
import { UseMutateFunction } from '@tanstack/react-query';
import { Alert, message, Modal as AntdModal, Spin } from 'antd';
import { AxiosResponse, isAxiosError } from 'axios';

import { RuleMessagesPanel } from '../rules';

export type ModalProps<T> = {
  itemId?: T;
  itemName?: string;
  onClose: () => void;
  onOk: UseMutateFunction<AxiosResponse, unknown, T, unknown>;
  onValidate?: UseMutateFunction<AxiosResponse, unknown, T, unknown>;
};

export const Modal = <T,>({ itemId, itemName, onOk, onClose, onValidate }: ModalProps<T>) => {
  const [messageApi, messageContextHolder] = message.useMessage();
  const [modal, contextHolder] = AntdModal.useModal();

  const ITEM_NAME = itemName ? `'${itemName}'` : 'this item';

  const handleOnOk = () => {
    if (!itemId) return;
    onOk(itemId, {
      onSuccess: (res) => {
        if (res.status === 200) {
          messageApi.open({
            type: 'success',
            content: `Successfully deleted ${ITEM_NAME}.`,
          });
        } else {
          messageApi.open({
            type: 'error',
            content: <RuleMessagesPanel title={`Failed to delete ${ITEM_NAME}`} messages={res.data?.messages} />,
          });
        }
        onClose();
      },
      onError: (error) => {
        messageApi.open({
          type: 'error',
          content: `Failed to delete ${ITEM_NAME}. Error: ${
            isAxiosError(error) ? error.response?.data['message'] : error
          }`,
        });
        onClose();
      },
    });
  };

  useEffect(() => {
    if (!itemId) return;

    const modalInstance = modal.confirm({
      maskClosable: false,
      title: `Deleting '${itemName ?? ''}'`,
      content: (
        <Spin tip="Confirming can delete..." style={{ width: '100%' }}>
          <div style={{ height: 50 }} />
        </Spin>
      ),
      okButtonProps: {
        disabled: true,
      },
      okType: 'danger',
      onOk: handleOnOk,
      onCancel: onClose,
    });

    if (onValidate) {
      onValidate(itemId, {
        onSuccess: (res) => {
          if (res.data.isSuccess) {
            // can delete
            modalInstance.update({
              content: (
                <RuleMessagesPanel title="Are you sure you want to delete this item?" messages={res.data.messages} />
              ),
              okButtonProps: {
                disabled: false,
              },
            });
          } else {
            // cannot delete
            modalInstance.update({
              content: (
                <Alert
                  message="Sorry, delete is not possible."
                  description={<RuleMessagesPanel messages={res.data.messages} />}
                  type="error"
                  showIcon
                ></Alert>
              ),
            });
          }
        },
        onError: (error) => {
          modalInstance.update({
            content: (
              <Alert
                message="Sorry, something went wrong and delete is not possible."
                description={error instanceof Error ? `Error: ${error.message}` : undefined}
                type="error"
                showIcon
              ></Alert>
            ),
          });
        },
      });
    } else {
      modalInstance.update({
        content: 'Are you sure you want to delete this item?',
        okButtonProps: {
          disabled: false,
        },
      });
    }

    // destroy modal instance
    // not 100% sure this is required but including it to be safe that we do not create
    // a memory leak.
    return () => {
      modalInstance && modalInstance.destroy();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemId, itemName, onValidate]); // only need to run effect when bundle changes

  return (
    <>
      {messageContextHolder}
      {contextHolder}
    </>
  );
};
